import React from "react";
import { Key } from "../enums";
import type { ColumnDefinition } from "../../types/ui";
import type { AnalyticsData, ExitTicketData } from "../../types/data";
import { renderLabel } from "../../renderers";
import ScrollableList from "../../components/cells/ScrollableList";
import { formatMs } from "../../service/stats";
import { calculateNeedsMoreSupport } from "../../types/utils";
import dayjs from "dayjs";
import TooltipWrapper from "../../components/TooltipWrapper";
import ExitTicketPopover from "../../components/cells/ExitTicketPopover";
import CustomTicketPopover from "../../components/cells/CustomTicketPopover";
import { CaretPosition } from "../../../../types/ui";

/**
 * A lesson built only from assessment slides has no content bites, so "goals
 * met" has no denominator. The cell reads N/A rather than 0/0 (NaN%), matching
 * how the assessment columns label a missing value; the hover title says why.
 */
const NO_GOALS_CELL = "N/A";
const NO_GOALS_TITLE = "No goals — this lesson has no bites";

const assessmentStatusInfo = React.createElement(
  "ul",
  { className: "list-disc pl-4 space-y-1" },
  React.createElement("li", null, "Mastered: 100% correct"),
  React.createElement(
    "li",
    null,
    "Partially mastered: greater than 50% but not 100%",
  ),
  React.createElement("li", null, "Not mastered: less than 50% correct"),
);

// Visual status is explicit and shared across single/multiple attempts. The
// attempt history remains in the popover; it must not change the badge shape.
const assessmentBadge = (label: string, status: string) =>
  React.createElement(
    "span",
    {
      className: "et-assessment-status",
      "data-status": status,
    },
    label,
  );

const createAssessmentColumn = (
  key: Key,
  label: string,
  accessor: (row: AnalyticsData) => ExitTicketData,
  assessmentLabel?: string,
): Partial<Record<Key, ColumnDefinition>> => ({
  [key]: {
    label,
    info: assessmentStatusInfo,
    key: (row: AnalyticsData) => accessor(row).status,
    sort: (row: AnalyticsData) => accessor(row).status,
    format: (v: string) => v,
    getLabel: (row: AnalyticsData) => accessor(row).label,
    render: (row: AnalyticsData) => {
      const { status, label: cellLabel, attempts } = accessor(row);
      const content = assessmentBadge(cellLabel, status);
      if (attempts.length > 0) {
        return React.createElement(TooltipWrapper, {
          ariaLabel: `${accessor(row).studentName}: ${assessmentLabel ?? label}`,
          trigger: content,
          tooltip: React.createElement(ExitTicketPopover, {
            data: accessor(row),
            assessmentLabel: assessmentLabel ?? label,
            onViewDetails: (attemptId: number) => {
              window.dispatchEvent(
                new CustomEvent("open-attempt-detail", {
                  detail: {
                    attemptId,
                    studentName: accessor(row).studentName,
                    assessmentLabel: assessmentLabel ?? label,
                  },
                }),
              );
            },
          }),
          caret: { position: CaretPosition.BOTTOM, color: "#C4C4C4" },
          width: "380px",
          tooltipClassName: "text-sm",
          wrapperClassName: "block w-full overflow-visible",
        });
      }
      return content;
    },
  },
});

export const columnConfig: Partial<Record<Key, ColumnDefinition>> = {
  [Key.STUDENT_NAME]: {
    label: "Student",
    key: (rowData: AnalyticsData) => rowData.studentName.value,
    sort: (rowData: AnalyticsData) => rowData.studentName.value,
    format: (v: string) => v,
    getLabel: (rowData: AnalyticsData) => rowData.studentName.label,
    render: (rowData: AnalyticsData) => {
      const needsMoreSupport = calculateNeedsMoreSupport(rowData.bites);
      return React.createElement(
        "div",
        { className: "font-bold" },
        renderLabel(
          rowData.studentName.label,
          needsMoreSupport
            ? "!text-[#FF7777] font-bold truncate min-w-0"
            : "truncate min-w-0",
        ),
      );
    },
  },
  [Key.CLASSROOM_NAME]: {
    label: "Group",
    key: (rowData: AnalyticsData) => rowData.classroomName.value,
    sort: (rowData: AnalyticsData) => rowData.classroomName.value,
    format: (v: string) => v,
    getLabel: (rowData: AnalyticsData) =>
      rowData.teacherName
        ? `${rowData.classroomName.label} — ${rowData.teacherName}`
        : rowData.classroomName.label,
    render: (rowData: AnalyticsData) => {
      const groupLabel = renderLabel(
        rowData.classroomName.label,
        "truncate min-w-0",
      );
      if (!rowData.teacherName) return groupLabel;

      return React.createElement(
        "div",
        { className: "min-w-0 w-full" },
        groupLabel,
        renderLabel(
          rowData.teacherName,
          "truncate min-w-0 text-xs et-analytics-placeholder text-[#B4B4B4]",
        ),
      );
    },
  },
  [Key.LESSON_NAME]: {
    label: "Lesson",
    key: (rowData: AnalyticsData) => rowData.lessonName.value,
    sort: (rowData: AnalyticsData) => rowData.lessonName.value,
    format: (v: string) => v,
    getLabel: (rowData: AnalyticsData) => rowData.lessonName.label,
    render: (rowData: AnalyticsData) => {
      const needsMoreSupport = calculateNeedsMoreSupport(rowData.bites);
      const content = renderLabel(
        rowData.lessonName.label,
        `${
          needsMoreSupport ? "!text-[#FF7777] font-semibold" : ""
        } truncate min-w-0 text-[13px] sm:text-sm`,
      );
      return React.createElement(TooltipWrapper, {
        ariaLabel: `Lesson: ${rowData.lessonName.label}`,
        trigger: content,
        tooltip: React.createElement(
          "div",
          { className: "font-mulish text-[#252525]" },
          rowData.lessonName.label,
        ),
        width: "320px",
        tooltipClassName: "text-sm",
        wrapperClassName: "block w-full overflow-hidden",
      });
    },
  },

  [Key.GOALS]: {
    label: "Goals Met",
    info: "The number of bites the students completed out of the total bites in the lesson.",
    key: (rowData: AnalyticsData) => {
      const { goalsMet, totalGoals } = rowData;
      return `${goalsMet}/${totalGoals}`;
    },
    sort: (rowData: AnalyticsData) => {
      const { goalsMet, totalGoals } = rowData;
      return `${goalsMet}/${totalGoals}`;
    },
    format: (v: string) => v,
    getLabel: (rowData: AnalyticsData) => {
      const { goalsMet, totalGoals } = rowData;
      if (totalGoals === 0) return NO_GOALS_TITLE;
      return `${goalsMet}/${totalGoals} (${Math.round((goalsMet / totalGoals) * 100)}%)`;
    },
    render: (rowData: AnalyticsData) => {
      const { goalsMet, totalGoals } = rowData;
      // An assessment-only lesson has no bites to complete, so there is no
      // ratio to show — printing 0/0 (NaN%) read as a broken row.
      if (totalGoals === 0)
        return renderLabel(
          React.createElement(
            "span",
            { className: "et-analytics-placeholder text-[#B4B4B4]" },
            NO_GOALS_CELL,
          ),
          "truncate min-w-0 text-right",
        );
      return renderLabel(
        React.createElement(
          "div",
          null,
          `${goalsMet}/${totalGoals} `,
          React.createElement(
            "span",
            { className: "italic" },
            ` (${Math.round((goalsMet / totalGoals) * 100)}%)`,
          ),
        ),
        "truncate min-w-0 text-right tabular-nums",
      );
    },
  },

  [Key.TIME_SPENT]: {
    label: "Time Spent",
    info: "Total time the student spent working on the lesson.",
    key: (rowData: AnalyticsData) => {
      const v = rowData.timeSpent;
      return formatMs(v);
    },
    sort: (rowData: AnalyticsData) => String(rowData.timeSpent),
    format: (v: string) => `${Math.round(Number(v) / 1000)}s`,
    getLabel: (rowData: AnalyticsData) => formatMs(rowData.timeSpent),
    render: (rowData: AnalyticsData) => {
      const v = rowData.timeSpent;
      return renderLabel(
        formatMs(v),
        "truncate min-w-0 text-right tabular-nums",
      );
    },
  },

  [Key.ASSIGN_DATE]: {
    label: "Assign Date",
    key: (rowData: AnalyticsData) => rowData.assignDate,
    sort: (rowData: AnalyticsData) => rowData.assignDate,
    format: (v: string) =>
      v ? dayjs(v).format("MMM DD, YYYY") : "Not Assigned",
    getLabel: (rowData: AnalyticsData) =>
      rowData.assignDate
        ? dayjs(rowData.assignDate).format("MMM DD, YYYY")
        : "Not Assigned",
    render: (rowData: AnalyticsData) =>
      renderLabel(
        rowData.assignDate
          ? dayjs(rowData.assignDate).format("MMM DD, YYYY")
          : "Not Assigned",
        "truncate min-w-0",
      ),
  },
  ...createAssessmentColumn(
    Key.PRE_ASSESSMENT,
    "Pre-Assessment",
    (row) => row.preAssessment,
  ),
  ...createAssessmentColumn(
    Key.EXIT_TICKET,
    "Exit Ticket",
    (row) => row.exitTicket,
  ),
  [Key.CUSTOM_TICKET]: {
    label: "Custom",
    info: "Supplementary pages such as surveys, reflections, or quick questionnaires.",
    key: (rowData: AnalyticsData) => rowData.customTicket.status,
    sort: (rowData: AnalyticsData) => rowData.customTicket.status,
    format: (v: string) => v,
    getLabel: (rowData: AnalyticsData) => rowData.customTicket.label,
    render: (rowData: AnalyticsData) => {
      const { status, label, attempts } = rowData.customTicket;
      // Derived from the data, not the label text: the cell wording changes
      // (it used to read "View") while "has something to open" does not.
      // `attempts` only ever holds completed attempts.
      const isSubmitted = attempts.length > 0;
      // Custom pages report submission, never mastery, even with a score.
      const content = assessmentBadge(
        label,
        isSubmitted ? "submitted" : status,
      );
      if (isSubmitted) {
        const dispatchDetail = (attemptId: number) =>
          window.dispatchEvent(
            new CustomEvent("open-attempt-detail", {
              detail: {
                attemptId,
                studentName: rowData.customTicket.studentName,
                assessmentLabel: "Custom",
              },
            }),
          );
        return React.createElement(TooltipWrapper, {
          ariaLabel: `${rowData.customTicket.studentName}: Custom assessment`,
          trigger: content,
          tooltip: React.createElement(CustomTicketPopover, {
            lessonId: rowData.lessonId,
            userId: rowData.userId,
            studentName: rowData.customTicket.studentName,
            onViewDetails: dispatchDetail,
          }),
          caret: { position: CaretPosition.BOTTOM, color: "#C4C4C4" },
          width: "380px",
          tooltipClassName: "text-sm",
          wrapperClassName: "block w-full overflow-visible",
          lazyRender: true,
        });
      }
      return content;
    },
  },
  [Key.BITES]: {
    label: "Total Bites",
    info: "This shows how many bites the lesson contains.",
    key: (rowData: AnalyticsData) =>
      rowData.bites.map((bite) => bite.biteId).join(","),
    sort: (rowData: AnalyticsData) =>
      rowData.bites.map((bite) => bite.biteId).join(","),
    format: (v: string) => v,
    getLabel: (rowData: AnalyticsData) =>
      `${rowData.bites.length} bite${rowData.bites.length !== 1 ? "s" : ""}`,
    render: (rowData: AnalyticsData) =>
      renderLabel(
        React.createElement(ScrollableList, {
          bites: rowData.bites,
          name: rowData.studentName.value,
          lessonName: rowData.lessonName.label,
        }),
      ),
  },
};
